import axios from "axios";

/**
 * The `code` a rejected native Stytch plugin call carries — mirrors
 * `NativeAuthErrorCode` in `client/ios/App/App/StytchAuthPlugin.swift`,
 * which maps the Stytch Swift SDK's `StytchAPIError.statusCode`/
 * `isUnrecoverableErrorType` onto one of these two values before calling
 * `call.reject(message, code)`. Any other/absent code is treated as
 * transient (see `isDefinitiveAuthRejection` below).
 */
export const NATIVE_AUTH_ERROR_CODE = {
    definitive: "definitive_auth_rejection",
    transient: "transient_auth_failure",
} as const;

const DEFINITIVE_HTTP_STATUSES = [400, 401, 403];

/** A refusal from a `fetch`-based refresh (the web embed's `/api/ai/embed/refresh`), which has
 *  no axios error to read the status from. */
export class AuthRefreshRejected extends Error {
    constructor(readonly status: number) {
        super(`Refresh failed with status ${status}`);
        this.name = "AuthRefreshRejected";
    }
}

/** The `code` property Capacitor attaches to a rejected native plugin call
 *  (the value passed to `call.reject(message, code)` on the Swift side). */
function pluginErrorCode(error: unknown): string | undefined {
    if (typeof error !== "object" || error === null || !("code" in error)) return undefined;
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
}

/**
 * True when `error` definitively proves a session is unauthorized/revoked —
 * never for network errors, timeouts, 5xx responses, or any error this
 * function can't structurally classify (the safe default: an unrecognized
 * failure reads as transient, so a durable native session survives a
 * transport/backend outage rather than being logged out on it).
 *
 * Classifies from structured fields only, never by parsing `error.message`:
 * - an axios error's `response.status`, or an `AuthRefreshRejected`'s `status`
 *   (the web embed's fetch-based token refresh)
 * - a rejected Capacitor plugin call's `code` property (native Stytch
 *   refresh path — see `NATIVE_AUTH_ERROR_CODE`'s doc comment)
 *
 * Both callers (`config/axios.ts`'s `recoverFromEmbedAuthFailure`,
 * `contexts/EmbedAuthContext.tsx`'s `refreshTokens`) used to each duplicate a
 * `/status\s+(\d{3})/` match against `error.message` instead. That missed
 * every native rejection: `StytchAuthPlugin.refresh` rejected with
 * `"Session refresh failed: \(error.localizedDescription)"`, which never
 * contains that substring, so a definitively revoked native session always
 * read as "transient" and was never signed out.
 */
export function isDefinitiveAuthRejection(error: unknown): boolean {
    if (error instanceof AuthRefreshRejected)
        return DEFINITIVE_HTTP_STATUSES.includes(error.status);
    if (axios.isAxiosError(error)) {
        return (
            error.response !== undefined && DEFINITIVE_HTTP_STATUSES.includes(error.response.status)
        );
    }
    return pluginErrorCode(error) === NATIVE_AUTH_ERROR_CODE.definitive;
}
